import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  makeStyles,
  shorthands,
  Button,
  Input,
  Checkbox,
  tokens,
  Card,
  Field,
  Title2,
  Subtitle2,
  MessageBar,
  MessageBarBody,
  Dropdown,
  Option,
} from '@fluentui/react-components';
import { apiConfig } from '../authConfig';
import { useAuth } from '../hooks/useAuth';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.padding('12px', '20px'),
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.gap(tokens.spacingVerticalL),
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
    ...shorthands.gap(tokens.spacingHorizontalL),
    alignItems: 'stretch',
  },
  sectionCard: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    ...shorthands.gap(tokens.spacingVerticalM),
  },
  sectionBody: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    ...shorthands.gap(tokens.spacingVerticalM),
  },
  fieldsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    ...shorthands.gap(tokens.spacingHorizontalM, tokens.spacingVerticalM),
  },
  formInput: {
    width: '100%',
    minWidth: 0,
  },
  checkboxGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    ...shorthands.gap(tokens.spacingHorizontalM, tokens.spacingVerticalS),
  },
  submitRow: {
    display: 'flex',
    ...shorthands.gap(tokens.spacingHorizontalS),
    alignItems: 'center',
  },
  messageBar: {
    width: '100%',
  },
});

const BOOL_FIELDS = [
  'IsEnabled', 'EnableFrozen', 'EnableMultiMachines',
  'EnableDegradeLogic', 'DualBase', 'IsCompliance', 'EnableFailover',
];

function PolarisCreateTestTypePage() {
  const classes = useStyles();
  const navigate = useNavigate();
  const { getAccessToken, accounts } = useAuth();

  const [form, setForm] = useState({
    TestType: '',
    Owner: '',
    MachineType: '',
    MachineCount: 1,
    EnvType: 'Polaris',
    IsEnabled: true,
    EnableFrozen: false,
    EnableMultiMachines: false,
    EnableDegradeLogic: false,
    DualBase: false,
    IsCompliance: false,
    RunMode: 'Iteration',
    EnableFailover: false,
    MultiQueueConfig: -1,
    Group: '',
    QueueTimeout: '',
    RunTimeout: '',
    TestLogRootPath: '',
    LogReaderSecurityGroup: '',
    ExternalMachinePool: '',
    ArgumentsMapping: '',
    FlipBedRule: '',
    ServiceLogLink: '',
  });

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(null);

  const updateField = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async () => {
    if (!form.TestType.trim() || !form.Owner.trim() || !form.MachineType.trim()) {
      setSubmitError('TestType, Owner, and MachineType are required.');
      return;
    }

    setSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(null);

    const params = { ...form };
    if (params.QueueTimeout === '') delete params.QueueTimeout;
    else params.QueueTimeout = parseInt(params.QueueTimeout, 10);
    if (params.RunTimeout === '') delete params.RunTimeout;
    else params.RunTimeout = parseInt(params.RunTimeout, 10);
    for (const key of ['Group', 'TestLogRootPath', 'LogReaderSecurityGroup', 'ExternalMachinePool', 'ArgumentsMapping', 'FlipBedRule', 'ServiceLogLink']) {
      if (!params[key]) delete params[key];
    }

    try {
      const token = await getAccessToken();
      const resp = await fetch(`${apiConfig.apiBaseUrl}/polaris/test-types`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(params),
      });
      if (!resp.ok) {
        const errData = await resp.json().catch(() => ({}));
        throw new Error(errData.message || `API error: ${resp.status}`);
      }
      const data = await resp.json();
      const status = Array.isArray(data) ? data[0]?.Status : data?.Status;
      if (status === 'Exists') {
        setSubmitError('TestType already exists.');
      } else {
        setSubmitSuccess(`TestType "${form.TestType}" created successfully!`);
        setTimeout(() => navigate(`/polaris/test-types/${encodeURIComponent(form.TestType)}`), 1500);
      }
    } catch (err) {
      setSubmitError(err.message || 'Failed to create test type. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const renderTextField = (label, key, placeholder, required) => (
    <Field key={key} label={label} required={required}>
      <Input
        className={classes.formInput}
        placeholder={placeholder}
        value={form[key]}
        onChange={(_e, d) => updateField(key, d.value)}
      />
    </Field>
  );

  const renderNumberField = (label, key, placeholder) => (
    <Field key={key} label={label}>
      <Input
        className={classes.formInput}
        placeholder={placeholder}
        value={String(form[key] ?? '')}
        onChange={(_e, d) => updateField(key, d.value)}
        type="number"
      />
    </Field>
  );

  const renderCheckboxField = (label, key) => (
    <Field key={key}>
      <Checkbox
        label={label}
        checked={form[key]}
        onChange={(_e, d) => updateField(key, d.checked)}
      />
    </Field>
  );

  const renderDropdownField = (label, key, options) => (
    <Field key={key} label={label}>
      <Dropdown
        className={classes.formInput}
        value={form[key]}
        selectedOptions={[form[key]]}
        onOptionSelect={(_e, d) => updateField(key, d.optionValue)}
      >
        {options.map(o => <Option key={o} value={o}>{o}</Option>)}
      </Dropdown>
    </Field>
  );

  return (
    <div className={classes.container}>
      <Title2>Create TestType</Title2>

      <div className={classes.formGrid}>
        <Card className={classes.sectionCard}>
          <Subtitle2>Required Details</Subtitle2>
          <div className={classes.sectionBody}>
            <div className={classes.fieldsGrid}>
              {renderTextField('TestType', 'TestType', undefined, true)}
              {renderTextField('Owner', 'Owner', undefined, true)}
              {renderTextField('MachineType', 'MachineType', undefined, true)}
            </div>
          </div>
        </Card>

        <Card className={classes.sectionCard}>
          <Subtitle2>Execution Settings</Subtitle2>
          <div className={classes.sectionBody}>
            <div className={classes.fieldsGrid}>
              {renderNumberField('MachineCount', 'MachineCount', '0-9')}
              {renderDropdownField('EnvType', 'EnvType', ['Polaris', 'PolarisTip', 'PolarisBox'])}
              {renderDropdownField('RunMode', 'RunMode', ['Iteration', 'Dynamic', 'Throughput', 'MultiResult'])}
              {renderNumberField('MultiQueueConfig', 'MultiQueueConfig', '-1')}
              {renderNumberField('QueueTimeout', 'QueueTimeout', undefined)}
              {renderNumberField('RunTimeout', 'RunTimeout', undefined)}
            </div>
          </div>
        </Card>

        <Card className={classes.sectionCard}>
          <Subtitle2>Feature Flags</Subtitle2>
          <div className={classes.sectionBody}>
            <div className={classes.checkboxGrid}>
              {BOOL_FIELDS.map(key => renderCheckboxField(key, key))}
            </div>
          </div>
        </Card>

        <Card className={classes.sectionCard}>
          <Subtitle2>Optional Settings</Subtitle2>
          <div className={classes.sectionBody}>
            <div className={classes.fieldsGrid}>
              {renderTextField('Group', 'Group', undefined)}
              {renderTextField('TestLogRootPath', 'TestLogRootPath', undefined)}
              {renderTextField('LogReaderSecurityGroup', 'LogReaderSecurityGroup', undefined)}
              {renderTextField('ExternalMachinePool', 'ExternalMachinePool', undefined)}
              {renderTextField('FlipBedRule', 'FlipBedRule', undefined)}
              {renderTextField('ServiceLogLink', 'ServiceLogLink', undefined)}
              {renderTextField('ArgumentsMapping', 'ArgumentsMapping', undefined)}
            </div>
          </div>
        </Card>
      </div>

      {submitError && (
        <MessageBar intent="error" className={classes.messageBar}>
          <MessageBarBody>{submitError}</MessageBarBody>
        </MessageBar>
      )}
      {submitSuccess && (
        <MessageBar intent="success" className={classes.messageBar}>
          <MessageBarBody>{submitSuccess}</MessageBarBody>
        </MessageBar>
      )}

      <div className={classes.submitRow}>
        <Button
          appearance="primary"
          onClick={handleSubmit}
          disabled={submitting || !form.TestType.trim() || !form.Owner.trim() || !form.MachineType.trim()}
          size="medium"
        >
          {submitting ? 'Creating...' : 'Create'}
        </Button>
      </div>
    </div>
  );
}

export default PolarisCreateTestTypePage;
